import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadCrumbs from '../../atoms/BreadCrumbs/BreadCrumbs';
import NavBar from '../../molecules/NavBar/NavBar';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { UserContext } from '../../../context/UserContext';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import {
  ButtonIcon,
  CloudUploadIcon,
} from '@rabbit/elements/shared-components';
import { ArrowBack } from '@mui/icons-material';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';

const isDemoEnv = import.meta.env.DEMO;

type LayoutProps = {
  children: React.ReactNode;
};

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const contextValues = useContext(UserContext);
  const { config } = useContext(AppContext);

  //TODO: remove Link and set back claimId
  const { docid, productName, title, warrantyId, modelNum, extraData } =
    state || {};

  const [breadCrumbLabel, setBreadCrumbLabel] = useState('');
  const { t } = useTranslation();

  const [canGoBack, setCanGoBack] = useState(false);

  type Crumb = { label: string; to?: string };
  const [breadCrumbItems, setBreadCrumbItems] = useState<Crumb[]>([]);

  useEffect(() => {
    const isPath = (p: string) => location.pathname === p;
    const starts = (p: string) => location.pathname.startsWith(p);

    const apply = (list: Crumb[]) => {
      setBreadCrumbItems(list);
      const label = list.map((i) => i.label).join(' > ');
      setBreadCrumbLabel(label);
      document.title = 'iWarranty Admin - ' + label;
    };

    const productsLabel = isDemoEnv
      ? t('general.productsAndParts')
      : t('general.products');

    type RouteConfig = { match: () => boolean; crumbs: () => Crumb[] };

    const breadcrumbRoutes: RouteConfig[] = [
      {
        match: () => isPath(SAGE_ROUTE_NAME.DASHBOARD),
        crumbs: () => [{ label: t('general.dashboard') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.CLAIMS),
        crumbs: () => [{ label: t('general.claims') }],
      },
      {
        match: () => starts(SAGE_ROUTE_NAME.CLAIMS + '/'),
        crumbs: () => {
          const child =
            docid && productName
              ? config.VENDABLES.SKU_SEARCH
                ? `${modelNum ?? extraData.modelNum} (${String(
                    productName
                  )}) - (${docid})`
                : `${String(productName)} (${docid})` || ''
              : '';
          return [
            { label: t('general.claims'), to: SAGE_ROUTE_NAME.CLAIMS },
            { label: child },
          ];
        },
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.MY_CLAIMS),
        crumbs: () => [{ label: t('general.myClaims') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.FAULTS),
        crumbs: () => [{ label: t('general.faults') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.MANAGE),
        crumbs: () => [{ label: t('general.manage') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.REGISTRATIONS),
        crumbs: () => [{ label: t('general.registrations') }],
      },
      {
        match: () => starts(SAGE_ROUTE_NAME.REGISTRATIONS + '/'),
        crumbs: () => {
          const regChild = `${warrantyId ?? ''} - ${
            config.VENDABLES.SKU_SEARCH
              ? `${modelNum || ''} (${title || ''})`
              : title || ''
          }`;
          return [
            {
              label: t('general.registrations'),
              to: SAGE_ROUTE_NAME.REGISTRATIONS,
            },
            { label: regChild },
          ];
        },
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.WARRANTY_OVERVIEW),
        crumbs: () => [{ label: t('general.warrantiesSummary') }],
      },
      {
        match: () => starts(SAGE_ROUTE_NAME.WARRANTY_OVERVIEW + '/'),
        crumbs: () => {
          const regChild = `${warrantyId ?? ''} - ${
            config.VENDABLES.SKU_SEARCH
              ? `${modelNum || ''} (${title || ''})`
              : title || ''
          }`;
          return [
            {
              label: t('general.warrantiesSummary'),
              to: SAGE_ROUTE_NAME.WARRANTY_OVERVIEW,
            },
            { label: regChild },
          ];
        },
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.PRODUCTS),
        crumbs: () => [{ label: productsLabel }],
      },
      {
        match: () => starts(SAGE_ROUTE_NAME.PRODUCTS + '/'),
        crumbs: () => [
          {
            label: productsLabel,
            to: `${SAGE_ROUTE_NAME.MANAGE}?tab=products`,
          },
          { label: productName ? String(productName) : '' },
        ],
      },
      {
        match: () => starts(SAGE_ROUTE_NAME.MANAGE + '/customer/'),
        crumbs: () => [
          { label: t('general.manage') },
          {
            label: t('general.customers'),
            to: `${SAGE_ROUTE_NAME.MANAGE}?tab=customers`,
          },
          { label: productName ? String(productName) : '' },
        ],
      },
      {
        match: () => starts(SAGE_ROUTE_NAME.MANAGE + '/partner/'),
        crumbs: () => [
          { label: t('general.manage') },
          {
            label: t('general.partners'),
            to: `${SAGE_ROUTE_NAME.MANAGE}?tab=partners`,
          },
          { label: productName ? String(productName) : '' },
        ],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.MANAGE_ACCOUNT),
        crumbs: () => [{ label: t('general.manageAccount') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.MY_ORGANIZATION),
        crumbs: () => [{ label: t('general.myOrganization') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.PLANS_AND_PRICES),
        crumbs: () => [{ label: 'Plans' }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.PRICING),
        crumbs: () => [{ label: 'Pricing' }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.USER_GROUP),
        crumbs: () => [{ label: t('general.userGroup') }],
      },
      {
        match: () => isPath(SAGE_ROUTE_NAME.PLATFORM_SERVICES_AGREEMENT),
        crumbs: () => [{ label: t('general.platformServicesAgreement') }],
      },
    ];

    for (const route of breadcrumbRoutes) {
      if (route.match()) {
        apply(route.crumbs());
        return;
      }
    }

    // Fallback: show path as title
    apply([{ label: location.pathname }]);
  }, [location, t, config]);

  useEffect(() => {
    // Show back button only if we have more than one breadcrumb (detail pages)
    setCanGoBack(breadCrumbItems.length > 1);
  }, [breadCrumbItems]);

  return (
    <div className="flex w-full flex-col">
      {location.pathname !== '/login' && <NavBar />}
      {contextValues?.user && breadCrumbLabel && state !== 'off' && (
        <div className="relative">
          <BreadCrumbs items={breadCrumbItems} />

          {canGoBack && (
            <ButtonIcon
              kind="bgLightGreen"
              onClick={() => navigate(-1)}
              label={'Go Back'}
              Icon={ChevronLeftIcon}
              iconLeft
              className={'absolute top-0 right-0 mt-4 mr-4'}
            />
          )}
        </div>
      )}
      <div className="flex w-full flex-grow flex-col px-8 py-8 md:m-[0_auto] md:max-w-[1280px] md:scroll-px-28">
        {children}
      </div>
    </div>
  );
}

export default Layout;
